/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.emptyMarkup = undefined; // "before typing" panel (sections/predictive-search-empty.liquid), fetched once
      // Search rewrites (Theme settings > Search > Search dropdown), e.g. "dumbbells = dumbbell": what is typed = what is fetched
      this.rewrites = (this.getAttribute('data-rewrites') || '')
        .split('\n')
        .map((line) => line.split('='))
        .filter((pair) => pair.length === 2 && pair[0].trim() && pair[1].trim())
        .map((pair) => [new RegExp(`\\b${pair[0].trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'), pair[1].trim()]);
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.showEmpty();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
  this.holdHeader(); // solid header straight away (homepage transparent header), before the panel loads
  const searchTerm = this.getQuery();
  if (!searchTerm.length) {
    this.showEmpty(); // popular searches, collections and best sellers before anything is typed
    return;
  }

  if (this.getAttribute('results') === 'true') {
    this.open();
  } else {
    this.getResults(searchTerm);
  }
}

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.collapse();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results (unless the "before typing" panel is available).
      if (!this.getQuery().length && !this.emptyMarkup) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      // No tabs any more: move through every result in the dropdown, in order.
      const allResults = [...this.results.querySelectorAll('.predictive-search__item')];
      const selectedResult = this.results.querySelector('.predictive-search__item[aria-selected="true"]');
      const index = allResults.indexOf(selectedResult);
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the previous result or the last one if we've reached the top of the list.
        resultToSelect = allResults[index - 1] || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result or the first one if we've reached the bottom of the list.
          resultToSelect = allResults[index + 1] || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let requestTerm = searchTerm;
      this.rewrites.forEach(([pattern, replacement]) => { requestTerm = requestTerm.replace(pattern, replacement); });

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(requestTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(requestTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[type]')}=query,product,collection,article,page`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      // Only products that can be bought (sold-out products are left out; "continue selling" / lead-time products stay)
      searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=hide`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Shows the "before typing" panel (popular searches, collections, best sellers).
     * Fetched once per page from sections/predictive-search-empty.liquid via the Section Rendering API.
     */
    async showEmpty() {
      if (this.emptyMarkup === undefined) {
        this.emptyMarkup = null; // loading
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const response = await fetch(`${root}?section_id=predictive-search-empty`);
          if (!response.ok) throw new Error(response.status);
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          const el = tmpl.content.querySelector('#shopify-section-predictive-search-empty');
          this.emptyMarkup = el ? el.innerHTML.replace(/psearch/g, this.input.id) : '';
        } catch (error) {
          this.emptyMarkup = '';
        }
      }

      // Only while the box is still empty and focused.
      if (!this.emptyMarkup || this.getQuery().length || document.activeElement !== this.input) return;
      this.results.innerHTML = this.emptyMarkup;
      this.removeAttribute('results');
      this.open();
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      // "View all results" shows what was typed (the request may have used a search rewrite)
      const allTerms = this.results.querySelector('.ps2__all-terms');
      if (allTerms) allTerms.textContent = `“${this.getQuery()}”`;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.setPanelTop();
      this.overlay.classList.add('is-visible');
        this.overlay.style.pointerEvents = '';
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      // document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      // this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
       this.overlayClickHandler = this.overlayClickHandler || ((evt) => {
        evt.stopPropagation();
        this.collapse(evt);
      });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      this.resizeHandler = this.resizeHandler || this.setPanelTop.bind(this);
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * The results panel is fixed and full width (snippets/predictive-search.liquid): start it below the search box, or
     * below the whole header on desktop, and pass the header container's content edges so its contents line up with it.
     */
    setPanelTop() {
      const header = this.closest('store-header');
      const box = this.closest('.header__search') || this;
      let top = Math.max(this.querySelector('.search').getBoundingClientRect().bottom, box.getBoundingClientRect().bottom);
      if (header) {
        top = Math.max(top, header.getBoundingClientRect().bottom);
        const container = header.querySelector('.container');
        if (container) {
          const rect = container.getBoundingClientRect();
          const styles = getComputedStyle(container);
          const left = rect.left + parseFloat(styles.paddingLeft);
          const right = document.documentElement.clientWidth - rect.right + parseFloat(styles.paddingRight);
          this.style.setProperty('--ps2-left', `${Math.max(0, Math.round(left))}px`);
          this.style.setProperty('--ps2-right', `${Math.max(0, Math.round(right))}px`);
        }
        this.holdHeader();
      }
      this.style.setProperty('--ps2-top', `${Math.max(0, Math.round(top))}px`);
    }

    /**
     * Clicking the dimmed page or pressing Escape closes the whole search (as the X does), so the header search box
     * collapses and the homepage header goes back to transparent. Elsewhere it just closes the results.
     * @param {object} evt - Event object.
     */
    collapse(evt) {
      const closeBtn = this.closest('store-header') ? this.querySelector('.search__close') : null;
      if (closeBtn) {
        closeBtn.click();
      } else {
        this.close(evt);
      }
    }

    /**
     * Solid header while search is in use (homepage transparent header). The theme removes the class when search closes.
     */
    holdHeader() {
      const header = this.closest('store-header');
      if (header && !header.classList.contains('search-is-visible-hold')) {
        header.classList.add('search-is-visible-hold');
        this.addedHeaderHold = true;
      }
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
close(evt) {
   const isReset = evt && evt.target === this.resetBtn;
  const isCloseBtn = evt && evt.target.closest && evt.target.closest('.search__close');

  if (isReset || isCloseBtn) {
    this.input.value = '';
    this.input.setAttribute('value', '');
    this.removeAttribute('results');
    if (isReset) this.input.focus(); // only re-focus for reset, not close
  }
  // If reset button was clicked, empty the input field.
  if (evt && evt.target === this.resetBtn) {
    this.input.value = '';
    this.input.setAttribute('value', '');  // ADD THIS
    this.removeAttribute('results');
    this.input.focus();
  }

  // Deselect the selected result (if there is one).
  const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
  if (selected) selected.setAttribute('aria-selected', 'false');

  this.removeAttribute('open');
  this.resetBtn.hidden = true;
  // this.overlay.classList.remove('is-visible');
  this.overlay.style.pointerEvents = 'none';
  this.input.setAttribute('aria-activedescendant', '');
  this.input.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('overlay-predictive-search');

  // Remove event listeners added on search results opening.
  this.removeEventListener('keydown', this.keydownHandler);
  this.removeEventListener('keyup', this.keyupHandler);  
  this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
  this.overlay.removeEventListener('click', this.overlayClickHandler);
  if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  if (this.addedHeaderHold) {
    // Keep the header solid while the search box itself is still showing (e.g. results closed with Escape)
    const header = this.closest('store-header');
    setTimeout(() => {
      if (header && !header.classList.contains('search-is-visible')) header.classList.remove('search-is-visible-hold');
    }, 100);
    this.addedHeaderHold = false;
  }
}
  }

  customElements.define('predictive-search', PredictiveSearch);
}
